/**
 * nexai.js — NexAI Frontend Client
 * Handles token exchange, streaming chat, summarise, draft, translate.
 */

const NEXAI_BASE = '';
const LS_TOKEN   = 'nx_nexai_token';

let _token       = localStorage.getItem(LS_TOKEN);
let _initialized = false;

export async function initNexAI(matrixAccessToken) {
  // Always refresh token on login — old token may be expired
  try {
    const res = await fetch(NEXAI_BASE + '/api/auth/matrix-token', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ matrixToken: matrixAccessToken }),
    });
    if (!res.ok) {
      // Fall back to cached token if refresh fails
      if (_token) { _initialized = true; return true; }
      return false;
    }
    const { token } = await res.json();
    _token = token;
    localStorage.setItem(LS_TOKEN, token);
    _initialized = true;
    return true;
  } catch (_) {
    // Fall back to cached token on network error
    if (_token) { _initialized = true; return true; }
    return false;
  }
}

export function isNexAIReady() { return _initialized && !!_token; }
export function getNexAIToken() { return _token || localStorage.getItem(LS_TOKEN) || null; }

export function clearNexAIToken() {
  _token = null;
  _initialized = false;
  localStorage.removeItem(LS_TOKEN);
}

/**
 * Stream a chat completion.
 * onChunk(text) called for each streamed token.
 * Returns full response text when done.
 */
export async function nexaiChat({ messages, workspaceName, channelName, roomContext, onChunk }) {
  if (!_token) throw new Error('NexAI not initialized');

  // Prepend room context as a system-style user message if provided
  let fullMessages = messages;
  if (roomContext) {
    fullMessages = [
      { role: 'user', content: 'Here is the recent conversation context for reference:\n\n' + roomContext },
      { role: 'assistant', content: 'I have the conversation context. How can I help you?' },
      ...messages,
    ];
  }

  const res = await fetch(NEXAI_BASE + '/api/nexai/chat', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + _token,
    },
    body: JSON.stringify({
      messages: fullMessages,
      workspaceName: workspaceName || 'NexChat',
      channelName:   channelName   || 'chat',
      stream: true,
    }),
  });

  if (res.status === 401) {
    clearNexAIToken();
    throw new Error('SESSION_EXPIRED');
  }
  if (res.status === 429) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Rate limit reached');
  }
  if (!res.ok) throw new Error('NexAI request failed');

  const reader  = res.body.getReader();
  const decoder = new TextDecoder();
  let fullText  = '';
  let buffer    = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const data = JSON.parse(line.slice(6));
        if (data.type === 'text' && data.text) {
          fullText += data.text;
          onChunk?.(data.text);
        }
        if (data.type === 'error') throw new Error(data.error);
      } catch (_) {}
    }
  }
  return fullText;
}

/**
 * Summarise a list of {senderName, content, timestamp} messages.
 */
export async function nexaiSummarise({ messages, channelName }) {
  if (!_token) throw new Error('NexAI not initialized');
  const res = await fetch(NEXAI_BASE + '/api/nexai/summarise', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + _token },
    body: JSON.stringify({ messages, channelName }),
  });
  if (!res.ok) throw new Error('Summarise failed');
  return res.json();
}

/**
 * Draft a reply given conversation context string.
 */
export async function nexaiDraft({ context, instruction }) {
  if (!_token) throw new Error('NexAI not initialized');
  const res = await fetch(NEXAI_BASE + '/api/nexai/draft', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + _token },
    body: JSON.stringify({ context, instruction }),
  });
  if (!res.ok) throw new Error('Draft failed');
  return res.json();
}
